'use strict';

import { contains, isEmpty } from 'underscore';

/**
 * Utility methods for Gen Ed flag related stuff.
 */
const GenEdUtils = {};

GenEdUtils.list = [
    {code: "C",     title: "English Composition",       hideCatalog: false,  genedCode:"course.genedrequirement.englishcomp_ind|uw.course.genedrequirement"},
    {code: "DIV",   title: "Diversity",                 hideCatalog: false,  genedCode:"course.genedrequirement.diversity_ind|uw.course.genedrequirement"},
    {code: "SSc",   title: "Social Science",            hideCatalog: false,  genedCode:"course.genedrequirement.aoi.ssc_ind|uw.course.genedrequirement"},
    {code: "NSc",   title: "Natural Science",           hideCatalog: false,  genedCode:"course.genedrequirement.aoi.nsc_ind|uw.course.genedrequirement"},
    {code: "RSN",   title: "Reasoning",                 hideCatalog: false,  genedCode:"course.genedrequirement.rsn_ind|uw.course.genedrequirement"},
    {code: "A&H",   title: "Arts and Humanities",       hideCatalog: false,  genedCode:"course.genedrequirement.aoi.ah_ind|uw.course.genedrequirement"},
    {code: "W",     title: "Additional Writing",        hideCatalog: true,   genedCode:"course.genedrequirement.writing_ind|uw.course.genedrequirement"}
];

GenEdUtils.exampleLabel = "A&H";
GenEdUtils.exampleList = ["A&H", "NSc", "SSc"];

/*
 * Styles of delimiters ...
 *
 *   WORDS == and or
 *   PUNCTUATION == , /
 */
GenEdUtils.DELIMITER = {
    WORDS: "WORDS",
    PUNCTUATION: "PUNCTUATION"
};

/**
 * The codes that get delimited with a slash. The others get commas.
 */
GenEdUtils.AREA_OF_INQUIRY_CODES = ["SSc", "NSc", "A&H"];

GenEdUtils.cache = [];

/**
 * Create a string of properly ordered and delimited gen ed codes.
 *
 * The logic goes like this ...
 *
 * Area of Inquiry (formerly "Area of Knowledge") codes are always separated with a slash indicating "or"
 * The rest are separated with a comma indicating "and".
 * The two groups always joined with a literal ", and " or " and " depending on whether there are multiple AoI codes.
 *
 * @param genEds A list of gen ed codes.
 * @param delimiter The style of delimiter to use.
 *
 * @returns A Label containing a term description.
 */
GenEdUtils.makeGenEdString = function(genEds, delimiter) {
    //  First group the genEds into two buckets
    let areaOfInquiryCodes = [];
    let basicSkillzCodes = [];
    let cacheKey = delimiter;
    for (let i = 0; i < genEds.length; i++) {
        let code = genEds[i];
        cacheKey = cacheKey + "," + code;
        let displayCode;
        if (code === 'sA&H') {
            displayCode = 'A&H';
        } else {
            displayCode = code;
        }
        if (contains(GenEdUtils.AREA_OF_INQUIRY_CODES, code)) {
            areaOfInquiryCodes.push(displayCode);
        } else {
            basicSkillzCodes.push(displayCode);
        }
    }

    if (!isEmpty(GenEdUtils.cache[cacheKey])) {
        return GenEdUtils.cache[cacheKey];
    }

    //  These could be empty or only have one item, but I'm going to sort them anyway. Fewer codes.
    areaOfInquiryCodes.sort();
    basicSkillzCodes.sort();

    // Set the delimiters
    let slash = "/";
    let comma = ", ";
    if (delimiter === GenEdUtils.DELIMITER.WORDS) {
        slash = " or ";
        comma = " and ";
    }

    // Do the formatting.
    // Update the operator when both C & W are present group them and separate them by "or".
    if (contains(basicSkillzCodes, 'C') && contains(basicSkillzCodes, 'W')) {
        basicSkillzCodes.push(basicSkillzCodes.shift() + slash +  basicSkillzCodes.pop());
        basicSkillzCodes.sort();
    }
    let aois = areaOfInquiryCodes.join(slash);
    let bs =  basicSkillzCodes.join(comma);

    let rVal = "";
    if (areaOfInquiryCodes.length > 0 && basicSkillzCodes.length > 0) {
        //  The delimiter between groups doesn't change
        if (areaOfInquiryCodes.length === 1) {
            rVal = aois + " and " + bs;
        } else {
            rVal = aois + ", and " + bs;
        }
    } else {
        if (aois.length === 0) {
            rVal = bs;
        } else {
            rVal = aois;
        }
    }

    GenEdUtils.cache[cacheKey] = rVal;

    return rVal;
};

GenEdUtils.getList = function() {
    return GenEdUtils.list;
};

/**
 * Sorts the genedList based on the title
 * @returns {*[]|*}
 */
GenEdUtils.getSortedTitleList = function() {
    let genedList = GenEdUtils.list;
    genedList.sort(function compare(a, b) {
        // Use toUpperCase() to ignore character casing
        const titleA = a.title.toUpperCase();
        const titleB = b.title.toUpperCase();

        let comparison = 0;
        if (titleA > titleB) {
            comparison = 1;
        } else if (titleA < titleB) {
            comparison = -1;
        }
        return comparison;
    });
    return genedList;
};

GenEdUtils.getTitleFromCode = function(code) {
    let description = "";
    GenEdUtils.list.map(function (genEd) {
        if (genEd.code === code) description = genEd.title;
    });
    return description;
};

GenEdUtils.makeGenEdAbbrFromCode = function(code, i) {
    let description = GenEdUtils.getTitleFromCode(code);
    return <abbr key={i} title={description} aria-label={description}>{code}</abbr>;
};

GenEdUtils.doesGenedCodeExist = function(code) {
    return GenEdUtils.getList().some(e => e.genedCode === code);
};

GenEdUtils.getShortCodeFromGenEdCode = (genedCode) => {
    let shortCode = "";
    GenEdUtils.list.map(function (genEd) {
        if (genEd.genedCode.startsWith(genedCode)) shortCode = genEd.code;
    });
    return shortCode;
}

export default GenEdUtils;